import { useState } from "react";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import {Car, Plane, Home, ShoppingBag, Download, Calculator} from 'lucide-react';

export default function LifestylePage() {
  const [commuteMode, setCommuteMode] = useState("car");
  const [commuteDistance, setCommuteDistance] = useState("");
  const [commuteDays, setCommuteDays] = useState("5");
  const [flightType, setFlightType] = useState("domestic-short");
  const [numFlights, setNumFlights] = useState("");
  const [waterUsage, setWaterUsage] = useState("");
  const [wasteProduced, setWasteProduced] = useState("");
  const [commuteEmissions, setCommuteEmissions] = useState<number | null>(null);
  const [flightEmissions, setFlightEmissions] = useState<number | null>(null);
  const [householdEmissions, setHouseholdEmissions] = useState<number | null>(null);
  
  const totalLifestyleEmissions = (
    (commuteEmissions || 0) + (flightEmissions || 0) + (householdEmissions || 0)
  );

  const calculateCommute = () => {
    const distance = parseFloat(commuteDistance) || 0;
    const days = parseInt(commuteDays) || 5;
    const weeksPerYear = 52;
    
    const emissionFactors: Record<string, number> = {
      "car": 0.404, // kg CO2 per mile
      "car-suv": 0.475,
      "car-hybrid": 0.275,
      "car-electric": 0.180,
      "bus": 0.089,
      "train": 0.041,
      "subway": 0.036,
      "bike": 0,
      "walk": 0,
      "motorcycle": 0.280,
      "carpool-2": 0.202,
      "carpool-3": 0.135,
    };
    
    const annual = (distance * emissionFactors[commuteMode] * days * weeksPerYear) / 1000;
    setCommuteEmissions(parseFloat(annual.toFixed(2)));
  };

  const calculateFlights = () => {
    const flights = parseFloat(numFlights) || 0;
    
    // Emission factors in tons CO2e per round trip
    const flightFactors: Record<string, number> = {
      "domestic-short": 0.18,  // < 500 miles
      "domestic-medium": 0.34, // 500-1500 miles
      "domestic-long": 0.60,   // > 1500 miles
      "international-short": 0.85, // < 3000 miles
      "international-long": 1.8,   // > 3000 miles
      "international-ultra": 3.5,  // > 6000 miles
    };
    
    const total = flights * flightFactors[flightType];
    setFlightEmissions(parseFloat(total.toFixed(2)));
  };
  
  const calculateHousehold = () => {
    const water = parseFloat(waterUsage) || 0;
    const waste = parseFloat(wasteProduced) || 0;
    
    // Water: ~0.0003 tons CO2 per gallon (treatment + heating)
    const waterEmissions = (water * 0.0003) / 1000;
    
    // Waste: ~0.4 tons CO2 per ton of waste to landfill
    const wasteEmissions = (waste * 0.4) / 1000;
    
    const total = waterEmissions + wasteEmissions;
    setHouseholdEmissions(parseFloat(total.toFixed(2)));
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Household & Lifestyle Greenhouse Gas Calculator",
    "url": "https://www.greenhousegascalculator.com/lifestyle",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Calculate emissions from daily commute, air travel, home heating, and household activities. Track your lifestyle carbon footprint with detailed breakdowns.",
    "featureList": "Daily commute greenhouse gas calculator, Air travel greenhouse gas calculator, Home heating emissions, Household carbon footprint, Lifestyle emissions tracking"
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Household & Lifestyle GHG Calculator | Daily Commute & Air Travel Emissions"
        description="Calculate emissions from daily commute, air travel, home heating, and household activities. Free greenhouse gas calculator for lifestyle and transportation choices."
        keywords="daily commute greenhouse gas calculator, air travel greenhouse gas calculator, household emissions, lifestyle carbon footprint, transportation emissions, home heating calculator"
        canonical="https://www.greenhousegascalculator.com/lifestyle"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Home className="w-10 h-10 text-[#2E8B57]" />
              <h1 className="text-4xl font-bold text-gray-900">
                Household & Lifestyle Calculator
              </h1>
            </div>
            <p className="text-xl text-gray-600">
              Calculate emissions from your daily commute, air travel, and household activities
            </p>
          </div>
        </div>
      </section>

      {/* Daily Commute Calculator */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <Car className="w-8 h-8 text-[#2E8B57]" />
              <h2 className="text-2xl font-bold text-gray-900">
                Daily Commute Greenhouse Gas Calculator
              </h2>
            </div>

            <div className="space-y-6 mb-8">
              <div>
                <label className="block font-semibold text-gray-900 mb-3">
                  Transportation Mode
                </label>
                <select
                  value={commuteMode}
                  onChange={(e) => setCommuteMode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                >
                  <option value="car">Car (Gasoline - Sedan)</option>
                  <option value="car-suv">Car (SUV/Truck)</option>
                  <option value="car-hybrid">Car (Hybrid)</option>
                  <option value="car-electric">Car (Electric)</option>
                  <option value="carpool-2">Carpool (2 people)</option>
                  <option value="carpool-3">Carpool (3+ people)</option>
                  <option value="motorcycle">Motorcycle</option>
                  <option value="bus">Bus</option>
                  <option value="train">Train</option>
                  <option value="subway">Subway</option>
                  <option value="bike">Bicycle</option>
                  <option value="walk">Walking</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-gray-900 mb-3">
                    Daily Round-Trip Distance (miles)
                  </label>
                  <input
                    type="number"
                    value={commuteDistance}
                    onChange={(e) => setCommuteDistance(e.target.value)}
                    placeholder="Enter miles"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block font-semibold text-gray-900 mb-3">
                    Days Per Week
                  </label>
                  <select
                    value={commuteDays}
                    onChange={(e) => setCommuteDays(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                  >
                    <option value="1">1 day</option>
                    <option value="2">2 days</option>
                    <option value="3">3 days</option>
                    <option value="4">4 days</option>
                    <option value="5">5 days</option>
                    <option value="6">6 days</option>
                    <option value="7">7 days</option>
                  </select>
                </div>
              </div>
            </div>

            <button
              onClick={calculateCommute}
              className="w-full bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
            >
              Calculate Commute Emissions
            </button>

            {commuteEmissions !== null && (
              <div className="mt-6 bg-white p-6 rounded-xl border-2 border-[#2E8B57]">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Annual Commute Emissions</p>
                  <p className="text-4xl font-bold text-[#2E8B57]">
                    {commuteEmissions} tons CO₂
                  </p>
                  <p className="text-sm text-gray-500 mt-2">per year</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Air Travel Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <Plane className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Air Travel Greenhouse Gas Calculator
              </h2>
            </div>

            <div className="space-y-6 mb-8">
              <div>
                <label className="block font-semibold text-gray-900 mb-3">
                  Flight Type
                </label>
                <select
                  value={flightType}
                  onChange={(e) => setFlightType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="domestic-short">Domestic Short (&lt;500 miles, e.g., NYC-DC)</option>
                  <option value="domestic-medium">Domestic Medium (500-1500 miles, e.g., NYC-Miami)</option>
                  <option value="domestic-long">Domestic Long (&gt;1500 miles, e.g., NYC-LA)</option>
                  <option value="international-short">International Short (&lt;3000 miles, e.g., US-Mexico)</option>
                  <option value="international-long">International Long (3000-6000 miles, e.g., US-Europe)</option>
                  <option value="international-ultra">International Ultra (&gt;6000 miles, e.g., US-Asia)</option>
                </select>
              </div>
              
              <div>
                <label className="block font-semibold text-gray-900 mb-3">
                  Number of Round-Trip Flights per Year
                </label>
                <input
                  type="number"
                  value={numFlights}
                  onChange={(e) => setNumFlights(e.target.value)}
                  placeholder="Enter number of flights"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Include both business and leisure travel
                </p>
              </div>
            </div>

            <button
              onClick={calculateFlights}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Calculate Flight Emissions
            </button>

            {flightEmissions !== null && (
              <div className="mt-6 bg-blue-50 p-6 rounded-xl border-2 border-blue-600">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Annual Flight Emissions</p>
                  <p className="text-4xl font-bold text-blue-600">
                    {flightEmissions} tons CO₂
                  </p>
                  <p className="text-sm text-gray-500 mt-2">per year</p>
                </div>
              </div>
            )}

            <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> Air travel is one of the most carbon-intensive activities per mile. Consider video conferencing for business meetings when possible, or choose direct flights to reduce emissions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Household Activities Calculator */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <Home className="w-8 h-8 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Household Activities Calculator
              </h2>
            </div>

            <div className="space-y-6 mb-8">
              <div>
                <label className="block font-semibold text-gray-900 mb-3">
                  Monthly Water Usage (gallons)
                </label>
                <input
                  type="number"
                  value={waterUsage}
                  onChange={(e) => setWaterUsage(e.target.value)}
                  placeholder="e.g., 3000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Average US household uses ~3,000 gallons/month
                </p>
              </div>
              
              <div>
                <label className="block font-semibold text-gray-900 mb-3">
                  Monthly Waste (pounds)
                </label>
                <input
                  type="number"
                  value={wasteProduced}
                  onChange={(e) => setWasteProduced(e.target.value)}
                  placeholder="e.g., 120"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Average person produces ~4 lbs of waste per day
                </p>
              </div>
            </div>

            <button
              onClick={calculateHousehold}
              className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Calculate Household Emissions
            </button>

            {householdEmissions !== null && (
              <div className="mt-6 bg-purple-50 p-6 rounded-xl border-2 border-purple-600">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Annual Household Emissions</p>
                  <p className="text-4xl font-bold text-purple-600">
                    {householdEmissions} tons CO₂e
                  </p>
                  <p className="text-sm text-gray-500 mt-2">per year from water & waste</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Equivalencies Visualization */}
      {totalLifestyleEmissions > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Your Lifestyle Impact
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                See your combined emissions from commute, flights, and household activities in real-world terms
              </p>
            </div>
            <EquivalenciesVisualizer emissions={totalLifestyleEmissions} className="mb-8" />
            
            <div className="flex justify-center mt-8">
              <button
                onClick={() => exportToPDF({
                  title: "Lifestyle & Household Carbon Footprint Report",
                  totalEmissions: totalLifestyleEmissions,
                  breakdown: {
                    commute: commuteEmissions || 0,
                    flights: flightEmissions || 0,
                    household: householdEmissions || 0,
                  },
                  calculationType: "lifestyle",
                })}
                className="bg-[#2E8B57] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Export Lifestyle Report as PDF
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Household Tips */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            Reduce Your Household Carbon Footprint
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Car className="w-6 h-6 text-[#2E8B57]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Transportation Tips
                </h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Carpool or use public transportation</li>
                <li>• Combine errands into single trips</li>
                <li>• Consider electric or hybrid vehicles</li>
                <li>• Walk or bike for short distances</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Home className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Home Energy Tips
                </h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Switch to LED light bulbs</li>
                <li>• Improve home insulation</li>
                <li>• Use programmable thermostats</li>
                <li>• Choose energy-efficient appliances</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Plane className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Travel Smart
                </h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Choose direct flights when flying</li>
                <li>• Use video conferencing when possible</li>
                <li>• Consider train travel for shorter distances</li>
                <li>• Carbon offset your flights</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <ShoppingBag className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Consumption Habits
                </h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Buy local and seasonal produce</li>
                <li>• Reduce meat consumption</li>
                <li>• Minimize food waste</li>
                <li>• Choose products with less packaging</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
